import { resolveMilestonePackageRoute } from '@/helpers/milestonePackageRoute';
import { useProjectSubmittedBefore2026 } from '@/hooks/useProjectSubmittedBefore2026';
import { useGetQuotesQuery } from '@/services/quotes';
import { Box, Button, Icon } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProgramType, QuoteStatus } from 'types';

import DateTimeDisplay from '../DateTime';

import { MilestoneBannerCopyVariant, MilestoneCompletionProps } from './milestoneBannerState';

type CompletionBannerProps = MilestoneCompletionProps & { className?: string };

type TextBodyProps = Pick<
  CompletionBannerProps,
  'currentPackage' | 'completedPackage' | 'completedAt' | 'copyVariant'
> & {
  programType: ProgramType;
};

const MilestoneCompletionBannerTextBody = ({
  currentPackage,
  completedPackage,
  completedAt,
  copyVariant,
  programType,
}: TextBodyProps) => {
  const knowledgeBaseUrl = useMemo(() => {
    switch (programType) {
      case ProgramType.energyBackup:
        return 'https://eb.palmetto.finance';
      case ProgramType.hvac:
        return 'https://hvac.palmetto.finance';
      case ProgramType.waterHeater:
        return 'https://waterheater.palmetto.finance'; // TODO: Update
      case ProgramType.generator:
        // [PLACEHOLDER] Generator KB URL — confirm with content team
        return 'https://generator.palmetto.finance';
      default:
        return 'https://help.palmetto.finance';
    }
  }, [programType]);

  const completedAtComponent = (
    <Box fontSize="base" direction={{ base: 'column', tablet: 'row' }} childGap="2xs" color="contrast">
      {completedAt && (
        <Box direction="row">
          <Box
            style={{
              paddingRight: '0.5rem',
            }}
          >
            <DateTimeDisplay fontSize="base" value={completedAt.toString()} dateFormat="DD" excludeTime />
          </Box>
          <Box
            style={{
              paddingRight: '0.1rem',
            }}
          >
            <DateTimeDisplay fontSize="base" value={completedAt.toString()} dateFormat="DD" showDuration />
          </Box>
          ago
        </Box>
      )}
    </Box>
  );

  switch (copyVariant) {
    case MilestoneBannerCopyVariant.ntpGranted:
      return (
        <Box flex="auto" childGap="2xs" padding="lg" fontSize="md" color="primary">
          <Box fontWeight="bold">Notice to Proceed Granted</Box>
          <Box fontWeight="medium" display={'inline-block'}>
            All point of sale requirements are complete. Your team may proceed at your discretion, within project
            guidelines detailed at{' '}
            <a
              href={knowledgeBaseUrl}
              style={{
                display: 'inline-block',
                color: 'inherit',
              }}
              target="_blank"
              rel="noreferrer"
            >
              {knowledgeBaseUrl}
            </a>
            .
          </Box>
          {completedAtComponent}
        </Box>
      );

    case MilestoneBannerCopyVariant.loopFieldComplete:
      return (
        <Box flex="auto" childGap="2xs" padding="lg" fontSize="md" color="primary">
          <Box fontWeight="bold">Loop Field Complete</Box>
          <Box fontWeight="medium">Your loop field has been approved. Submit your Install Package to continue.</Box>
          {completedAtComponent}
        </Box>
      );

    case MilestoneBannerCopyVariant.installApproved:
      return (
        <Box flex="auto" childGap="2xs" padding="lg" fontSize="md" color="primary">
          <Box fontWeight="bold">{completedPackage?.name} Approved</Box>
          <Box fontWeight="medium">
            Submit for Activation milestone as soon as possible. Note that Proof of PTO may be submitted in advance
            of the full Activation Package.
          </Box>
          {completedAtComponent}
        </Box>
      );

    case MilestoneBannerCopyVariant.generic:
    default:
      return (
        <Box flex="auto" childGap="2xs" padding="lg" fontSize="md" color="primary">
          <Box fontWeight="bold">{completedPackage?.name} Approved</Box>
          <Box fontWeight="medium">Good work! Please submit your {currentPackage?.name} for the next milestone.</Box>
          {completedAtComponent}
        </Box>
      );
  }
};

const MilestoneCompletionBanner = ({
  completedPackage,
  currentPackage,
  account,
  hasDesignDocsQueue,
  hasPtoFlagsEnabled,
  className,
  completedAt,
  copyVariant,
  ptoQueue,
}: CompletionBannerProps) => {
  const programType = account?.programType;
  const { data: quotes, isLoading: areQuotesLoading } = useGetQuotesQuery(
    { id: account?.id, programType },
    { skip: !programType },
  );
  const wasSubmittedBefore2026 = useProjectSubmittedBefore2026(account?.id);
  const navigate = useNavigate();

  const handleViewNavigate = () => {
    const hasAttestment =
      account?.systemDesign && 'attestment' in account.systemDesign
        ? Boolean(account.systemDesign.attestment)
        : false;
    const hasActiveQuote = areQuotesLoading
      ? false
      : Boolean((quotes || []).find((element: any) => element.status === QuoteStatus.active));
    const isSolarPR = programType === ProgramType.solar && account?.address?.state?.toUpperCase() === 'PR';

    const route = resolveMilestonePackageRoute({
      accountId: account?.id,
      programType,
      packageType: currentPackage.type,
      isSolarPR,
      hasActiveQuote,
      hasAttestment,
      wasSubmittedBefore2026,
      hasDesignDocsQueue,
      hasPtoQueue: Boolean(ptoQueue),
      hasPtoFlagsEnabled,
    });

    navigate(route.path, route.state ? { state: route.state } : undefined);
  };

  return (
    <Box
      borderWidth="xxs"
      borderColor="primary"
      radius="md"
      direction={{ base: 'column', tablet: 'row' }}
      alignItems="stretch"
      fontSize="sm"
      overflow="hidden"
      shadow="xs"
      height="auto"
      className={className}
      background="badge-primary"
    >
      <Box padding="lg" direction="row">
        <Icon name="celebration" size="2xl" color="primary" />
      </Box>
      <MilestoneCompletionBannerTextBody
        completedPackage={completedPackage}
        currentPackage={currentPackage}
        completedAt={completedAt}
        copyVariant={copyVariant}
        programType={programType}
      />
      <Box padding={{ base: '0 lg lg lg', tablet: 'lg' }} justifyContent="center">
        <Button
          variant="primary"
          tone="primary"
          size={{ base: 'sm', desktop: 'md' }}
          aria-label="view completed milestone"
          navigate={handleViewNavigate}
          isDisabled={areQuotesLoading}
        >
          Submit Package
        </Button>
      </Box>
    </Box>
  );
};

export default MilestoneCompletionBanner;
